/**
 * Tenant SPA sidebar — brand-aware shell.
 *
 *   bg     = owner.brandColors.primary (shared useBrandPrimary resolver)
 *   accent = BRAND_ACCENT — active border + hover text (a sensible neutral
 *            that reads on any owner primary; replace with a per-owner
 *            accent field if owners need to fully repaint this rail).
 *
 * The brand mark uses the owner's `brandLogoOnPrimaryUrl` — the variant
 * the owner uploaded specifically for the primary surface. No auto-
 * inversion. If only the on-white logo has been uploaded, we fall back
 * to the owner name as text (rather than mis-rendering a dark logo on
 * a dark surface). User footer at the bottom: name, email, sign-out.
 */

import { ReactNode, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/clerk-react';
import {
  LayoutDashboard,
  FileText,
  FileCheck,
  FileUp,
  BellRing,
  Building2,
  ChevronRight,
  ClipboardCheck,
  LifeBuoy,
  Users,
  UsersRound,
  Settings,
  LogOut,
  UserCog,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useBranding, useBrandPrimary } from '../branding-context.js';
import { BRAND_ACCENT } from '../owner-branding.js';
import { tenantApi } from '../api.js';
import { useTenantQuery } from '../query.js';

type NavItem = {
  title: string;
  to: string;
  icon: LucideIcon;
  /** Optional small badge count rendered next to the label. */
  badge?: number;
  /** Path prefixes under `to` that should NOT activate this item (sibling nested routes). */
  excludePrefixes?: string[];
  /** Match `to` exactly (no prefix match). Needed for `to="/"` so it isn't always active. */
  end?: boolean;
};

const mainNav: NavItem[] = [
  { title: 'Översikt', to: '/', icon: LayoutDashboard, end: true },
  { title: 'Mallar', to: '/templates', icon: FileText },
  {
    title: 'Avtal',
    to: '/contracts',
    icon: FileCheck,
    excludePrefixes: ['/contracts/import'],
  },
  { title: 'Importera', to: '/contracts/import', icon: FileUp },
  {
    title: 'Bevakningar',
    to: '/oversikt/avtal-under-bearbetning',
    icon: BellRing,
  },
  { title: 'Granskningar', to: '/reviews', icon: ClipboardCheck },
  { title: 'Fastigheter', to: '/properties', icon: Building2 },
];

const managementNav: NavItem[] = [
  { title: 'Användare', to: '/users', icon: Users },
  { title: 'Grupper', to: '/groups', icon: UsersRound },
  { title: 'Inställningar', to: '/settings', icon: Settings },
];

export function Sidebar({
  mobileOpen = false,
  onMobileClose,
}: {
  /** Drives the off-canvas drawer below the `lg` breakpoint. */
  mobileOpen?: boolean;
  /** Close the drawer (called on backdrop click, Escape, and nav-link click). */
  onMobileClose?: () => void;
} = {}) {
  const { branding } = useBranding();
  const { user } = useUser();
  const { signOut, openUserProfile } = useClerk();
  const location = useLocation();
  const ownerLogo = branding?.owner.brandLogoOnPrimaryUrl ?? null;
  const ownerName = branding?.owner.name ?? 'Regta';
  const tenantName = branding?.tenant.name ?? null;
  const primary = useBrandPrimary();

  // Pending reviews assigned to the actor — drives the small badge on
  // the "Granskningar" item. Single GET, cheap to keep fresh.
  const reviewsQ = useTenantQuery(
    'reviews.list_my_pending',
    () => tenantApi.reviews.list_my_pending(),
    [],
    { ignoreActiveGroup: true },
  );
  const pendingReviewCount = reviewsQ.data?.reviews.length ?? 0;

  // Currently-due reminders — drives the badge on "Bevakningar". Same
  // endpoint/key/options as the header NotificationsBell, so SWR dedupes
  // to a single request and the two surfaces stay in lockstep (live poll
  // + revalidate-on-focus, not just on a full page refresh).
  const dueNotificationsQ = useTenantQuery(
    'notifications.list_due',
    () => tenantApi.notifications.list_due({ limit: 10 }),
    [],
    {
      ignoreActiveGroup: true,
      refreshInterval: 60_000,
      revalidateOnFocus: true,
    },
  );
  const dueNotificationCount = dueNotificationsQ.data?.total ?? 0;

  const navWithBadges: NavItem[] = mainNav.map((item) => {
    if (item.to === '/reviews' && pendingReviewCount > 0) {
      return { ...item, badge: pendingReviewCount };
    }
    if (
      item.to === '/oversikt/avtal-under-bearbetning' &&
      dueNotificationCount > 0
    ) {
      return { ...item, badge: dueNotificationCount };
    }
    return item;
  });

  // Auto-close the mobile drawer whenever the route changes (user
  // tapped a nav link or programmatic navigation occurred).
  useEffect(() => {
    if (mobileOpen) onMobileClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Escape to close + body scroll lock while the drawer is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMobileClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen, onMobileClose]);

  return (
    <>
      {/* Mobile/tablet backdrop. Tap to close. Hidden on lg+. */}
      <div
        aria-hidden="true"
        onClick={onMobileClose}
        className={[
          'fixed inset-0 z-30 bg-black/40 backdrop-blur-[1px] transition-opacity duration-200 lg:hidden',
          mobileOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none',
        ].join(' ')}
      />

      <aside
        className={[
          // Desktop: sticky rail inside the shell flex row.
          'lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-60 lg:shrink-0 lg:translate-x-0',
          // Mobile/tablet: off-canvas drawer.
          'fixed inset-y-0 left-0 z-40 flex w-72 max-w-[85vw] flex-col transition-transform duration-200 ease-out',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        ].join(' ')}
        style={{ backgroundColor: primary }}
        aria-label="Sidomeny"
      >
        {/* Drawer close button — visible only when the drawer is rendered as an overlay. */}
        <button
          type="button"
          onClick={onMobileClose}
          aria-label="Stäng meny"
          className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full text-white/70 hover:bg-white/10 hover:text-white lg:hidden"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="px-5 py-8">
          {ownerLogo ? (
            <img
              src={ownerLogo}
              alt={ownerName}
              className="h-8 w-auto"
            />
          ) : (
            <div className="text-base font-semibold tracking-tight text-white">
              {ownerName}
            </div>
          )}
          {tenantName && (
            <div className="mt-2 text-[11px] text-white/55">För {tenantName}</div>
          )}
        </div>

        <div className="mx-3 h-px bg-white/10" />

        <nav className="flex-1 overflow-y-auto py-4">
          <NavGroup label="Huvudmeny">
            {navWithBadges.map((item) => (
              <SidebarLink key={item.to} item={item} />
            ))}
          </NavGroup>
          <NavGroup label="Hantering">
            {managementNav.map((item) => (
              <SidebarLink key={item.to} item={item} />
            ))}
          </NavGroup>
        </nav>

        <div className="mx-3 h-px bg-white/10" />

        <div className="px-3 pt-3 pb-1">
          <Link
            to="/support"
            className="group flex items-center gap-3 rounded-md border border-white/15 bg-white/5 px-3 py-2.5 text-left transition-colors hover:border-white/30 hover:bg-white/10"
          >
            <LifeBuoy className="h-4 w-4 shrink-0 text-white/70 group-hover:text-white" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-white/90">
                Behöver du hjälp?
              </div>
              <div className="truncate text-[10px] text-white/60">
                support@regta.se
              </div>
            </div>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-white/50 transition-transform group-hover:translate-x-0.5 group-hover:text-white/80" />
          </Link>
        </div>

        <div className="px-3 py-4">
          {user && (
            <button
              type="button"
              onClick={() => openUserProfile()}
              title="Hantera profil"
              className="group mb-2 flex w-full items-center gap-2 rounded-md px-1 py-1.5 text-left transition-colors hover:bg-white/10"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-white">
                  {user.fullName ?? user.primaryEmailAddress?.emailAddress}
                </p>
                {user.primaryEmailAddress && (
                  <p className="truncate text-[10px] text-white/60">
                    {user.primaryEmailAddress.emailAddress}
                  </p>
                )}
              </div>
              <UserCog className="h-4 w-4 shrink-0 text-white/50 transition-colors group-hover:text-white" />
            </button>
          )}
          <button
            type="button"
            onClick={() => signOut()}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-white/70 hover:bg-white/10 hover:text-white"
          >
            <LogOut className="h-4 w-4" />
            Logga ut
          </button>
        </div>
      </aside>
    </>
  );
}

function NavGroup({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-2">
      <div className="px-5 py-2 text-[9px] font-medium uppercase tracking-[0.2em] text-white/40">
        {label}
      </div>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  const location = useLocation();
  const excluded = item.excludePrefixes?.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );
  // The Översikt entry (`to="/"`) is end-matched so it doesn't activate on
  // every route, but we still want it lit while the user is on the
  // "Så fungerar Regta" instructions subpage. The Bevakningar page (also
  // under /oversikt/*) has its own nav entry and lights up there instead.
  const overviewSub =
    item.to === '/' && location.pathname === '/oversikt/sa-fungerar-regta';
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          [
            'group flex items-center gap-3 border-l-2 px-5 py-2 text-sm transition-colors',
            (isActive || overviewSub) && !excluded
              ? 'border-l-[var(--hb-lime)] text-white'
              : 'border-l-transparent text-white/70 hover:text-white',
          ].join(' ')
        }
        style={{ ['--hb-lime' as never]: BRAND_ACCENT }}
      >
        <Icon className="h-4 w-4 opacity-70" />
        <span className="flex-1">{item.title}</span>
        {item.badge ? (
          <span className="rounded-full bg-white/15 px-1.5 text-[10px] font-medium text-white">
            {item.badge}
          </span>
        ) : null}
      </NavLink>
    </li>
  );
}
